/* =====================================================================================
   Who changed what, and when.

   The technical record is filled in by whoever visited the laboratory, from memory, and
   corrected later by someone who was not there. Without a history a disputed figure has no
   provenance and the last person to type wins.

   Written by database trigger rather than by this application, so it records changes made
   through any route: this form, a direct API call, or a script. An audit trail the caller can
   forget to write is not an audit trail.
   ===================================================================================== */

import { useEffect, useState } from 'react';
import { fetchTechnicalAudit, type AuditEntry } from '../lib/technical';
import { onFail } from '../lib/errors';

const FIELD_LABELS: Record<string, string> = {
  cfdna_tube: 'cfDNA collection tube',
  cfdna_tube_other: 'cfDNA tube (other)',
  plasma_input_ml: 'Plasma input (mL)',
  plasma_separation_hrs: 'Time to plasma separation (h)',
  cfdna_extraction_kit: 'cfDNA extraction kit',
  hla_resolution: 'HLA resolution',
  chimerism_method: 'Chimerism method',
  annual_typings: 'Annual typings',
  annual_crossmatches: 'Annual crossmatches',
  turnaround_hours_routine: 'Turnaround, routine',
  turnaround_hours_urgent: 'Turnaround, urgent',
  staffing_wte: 'Staffing (WTE)',
  vendor: 'Vendor', product: 'Product', ends_on: 'Commitment ends',
  notice_months: 'Notice period', satisfaction: 'Satisfaction',
  displaceable: 'Displaceable', commitment: 'Commitment type',
};

export function TechnicalHistory({ siteId }: { siteId: string }) {
  const [rows, setRows] = useState<AuditEntry[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchTechnicalAudit(siteId).then(setRows).catch(onFail('Change history', () => setRows([])));
  }, [siteId]);

  if (!rows.length) return null;

  return (
    <details className="tech-history" open={open}
             onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>
        Change history
        <span className="th-count">{rows.length} change{rows.length === 1 ? '' : 's'}</span>
      </summary>
      <ol className="th-list">
        {rows.map((r) => (
          <li key={r.id}>
            <div className="th-field">{FIELD_LABELS[r.field] ?? r.field}</div>
            <div className="th-change">
              <span className="th-old">{r.old_value ?? 'not recorded'}</span>
              <span className="th-arrow" aria-label="changed to">→</span>
              <span className="th-new">{r.new_value ?? 'cleared'}</span>
            </div>
            <div className="th-meta">
              {new Date(r.changed_at).toLocaleString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
              {r.rationale && <span className="th-why"> · {r.rationale}</span>}
            </div>
          </li>
        ))}
      </ol>
    </details>
  );
}
